/* eslint-disable react-refresh/only-export-components -- FAQ copy builder + list component share one module by design */
import React from 'react';
import { ChevronDown } from 'lucide-react';
import { formatDeviceList, resolveStationDevices } from '../streamingDeviceDisplay';

export interface AdvertiserFaqItem {
  q: string;
  a: React.ReactNode;
}

/**
 * Shared advertiser FAQ copy used on the landing page and Help menu.
 * CTV-810: the delivery answer names the station's own selected devices
 * (empty selection falls back to the full canonical set). The "What is
 * connected TV" answer stays generic on purpose; it defines CTV as a
 * category rather than describing this station.
 */
export function buildAdvertiserFaq(publisherName: string, devices?: string[]): AdvertiserFaqItem[] {
  const deviceList = formatDeviceList(resolveStationDevices(devices).map((d) => d.label));
  return [
    {
      q: 'Where will my ads be delivered?',
      a: (
        <>
          {/* Copy shape per Seth's CTV-810 review (2026-08-31). */}
          Anywhere {publisherName} is streaming their content: {deviceList}.
        </>
      ),
    },
    {
      q: 'What is connected TV (CTV)?',
      a: (
        <>
          Connected TV is video advertising delivered through streaming apps on smart TVs, Roku, Fire
          TV, Apple TV, and similar devices. Your commercial plays just like a traditional TV ad,
          but only to viewers in {publisherName}'s local market.
        </>
      ),
    },
    {
      q: 'How long until my campaign goes live?',
      a: (
        <>
          Most campaigns are reviewed and approved within 24–48 hours. You pick a start date that's at
          least 3 days out so {publisherName} has time to review your commercial.
        </>
      ),
    },
    {
      q: "What if I don't have a commercial?",
      a: (
        <>
          No problem. This platform includes a free built-in video creator that helps you turn your
          message into a polished, CTV-ready commercial in minutes. No production experience required.
          You will find it on the commercial step of the campaign wizard, where it builds a first
          draft from your website while you set up the rest of your campaign.
        </>
      ),
    },
    {
      q: 'Is there a minimum budget?',
      a: (
        <>
          Minimum budget is as low as $250. This helps ensure your campaign reaches enough viewers to
          make an impact.
        </>
      ),
    },
    {
      q: 'Will I receive a campaign performance report?',
      a: (
        <>
          Yes, you can monitor your campaign's performance in real time with up-to-date reporting and
          key performance metrics.
        </>
      ),
    },
  ];
}

/**
 * CTV-775: the AI commercial FAQ. A second list beside the general one,
 * rendered in the Help menu FAQ modal under its own subheading and inside
 * the expanded Waymark panel behind "How does this work?", never on the
 * station landing page. Placeholder copy: a first draft the team revises
 * after feedback. Buyer-facing words only ("AI commercial", never "link" or
 * "generation"; see CONTEXT.md, Waymark improvements).
 */
export function buildAiCommercialFaq(publisherName: string): AdvertiserFaqItem[] {
  return [
    {
      q: "What if I don't like the commercial it made?",
      a: (
        <>
          You have two options. Choose <strong>Start over</strong> to throw the current version away
          and build a fresh one from the same website. Or open <strong>Review</strong> to keep the
          parts you like and change the words, images, and pacing yourself.
        </>
      ),
    },
    {
      q: 'Can I make a commercial for a specific offer or event?',
      a: (
        <>
          Yes. Point the builder at the page on your website that describes the offer (a sale, a
          seasonal special, an event) instead of your home page, and it will draft around that. You
          can also open Review afterward and rewrite the script to say exactly what you want.
        </>
      ),
    },
    {
      q: 'What can I edit?',
      a: (
        <>
          The script and on-screen text, the images and clips, the colors and logo, the voiceover,
          and the music. Every change re-renders a broadcast-quality version, so what you see in the
          editor is what airs on {publisherName}.
        </>
      ),
    },
    {
      q: 'Can I run more than one AI commercial in a campaign?',
      a: (
        <>
          Yes. Once a commercial is in your campaign, choose <strong>Make another commercial</strong>{' '}
          to build an additional one from the same website. Each one is its own commercial in your
          rotation, so you can compare them or run different messages at once.
        </>
      ),
    },
  ];
}

interface AdvertiserFaqListProps {
  accentColor: string;
  publisherName: string;
  /** CTV-810: the station's selected devices, for the delivery answer. */
  devices?: string[];
  /** Slightly tighter padding for the help modal. */
  compact?: boolean;
  /**
   * CTV-775: which list to render. Defaults to the general advertiser FAQ;
   * 'ai-commercial' renders buildAiCommercialFaq.
   */
  faq?: 'advertiser' | 'ai-commercial';
  /** CTV-775: fires when a question is opened (index + question text). */
  onOpen?: (index: number, question: string) => void;
}

export const AdvertiserFaqList: React.FC<AdvertiserFaqListProps> = ({
  accentColor,
  publisherName,
  devices,
  compact = false,
  faq = 'advertiser',
  onOpen,
}) => {
  const items = React.useMemo(
    () =>
      faq === 'ai-commercial'
        ? buildAiCommercialFaq(publisherName)
        : buildAdvertiserFaq(publisherName, devices),
    [faq, publisherName, devices],
  );
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const padX = compact ? 'px-4' : 'px-5';
  const padY = compact ? 'py-3.5' : 'py-4';

  return (
    <div
      className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden divide-y divide-slate-200/70"
      style={{ boxShadow: 'var(--card-shadow)' }}
    >
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={item.q} className="relative">
            <span
              aria-hidden
              className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r transition-opacity"
              style={{
                backgroundColor: accentColor,
                opacity: isOpen ? 1 : 0,
              }}
            />
            <button
              type="button"
              onClick={() => {
                if (!isOpen) onOpen?.(i, item.q);
                setOpenIndex(isOpen ? null : i);
              }}
              className={`w-full flex items-center gap-3 ${padX} ${padY} text-left transition-colors hover:bg-slate-50/60`}
              aria-expanded={isOpen}
            >
              <span className="flex-1 text-[14px] font-semibold tracking-tight text-slate-900">
                {item.q}
              </span>
              <ChevronDown
                className="w-4 h-4 transition-transform duration-200 shrink-0"
                style={{
                  color: accentColor,
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </button>
            <div
              className="overflow-hidden transition-all duration-300 ease-out"
              style={{
                maxHeight: isOpen ? '400px' : '0px',
                opacity: isOpen ? 1 : 0,
              }}
            >
              <div className={`${padX} pb-4 -mt-1 text-[13px] text-slate-600 leading-relaxed`}>
                {item.a}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
